import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const TITLE = { purchase_order: "PURCHASE ORDER", sale: "TAX INVOICE", estimate: "QUOTATION", proforma: "PROFORMA INVOICE" };

export default function ShareDoc() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/share/${token}`).then(({ data }) => setDoc(data)).catch(() => setErr("This link is invalid or has expired."));
  }, [token]);

  if (err) return <div className="p-10 text-center text-slate-600" data-testid="share-error">{err}</div>;
  if (!doc) return <div className="p-10">Loading…</div>;
  const { txn: t, brand: b } = doc;

  return (
    <div className="min-h-screen bg-slate-100 py-6 invoice-page-wrap">
      <div className="no-print max-w-3xl mx-auto flex justify-end mb-2">
        <Button className="btn-primary" onClick={() => window.print()} data-testid="share-print-btn"><Printer size={14} /> Print / Save as PDF</Button>
      </div>
      <div className="invoice-a4 tpl-classic print-area" data-testid="share-doc">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-3">
          <div>
            <div className="text-2xl font-extrabold tracking-tight">{b.name}</div>
            <div className="text-xs">{b.address}</div>
            <div className="text-xs">{b.state} · {b.phone} · {b.email}</div>
            {b.gstin && <div className="text-xs font-mono">GSTIN: {b.gstin}</div>}
          </div>
          <div className="text-right">
            <div className="text-xl font-black">{TITLE[t.txn_type] || t.txn_type.toUpperCase()}</div>
            <div className="text-xs font-mono mt-1">No: {t.number}</div>
            <div className="text-xs font-mono">Date: {new Date(t.date).toLocaleDateString()}</div>
            {t.expected_date && <div className="text-xs font-mono">Deliver by: {t.expected_date.slice(0, 10)}</div>}
          </div>
        </div>
        <div className="mt-3 text-sm"><span className="overline">To</span><div className="font-semibold">{t.party_name}</div></div>
        <table className="w-full mt-4 text-xs border border-slate-300">
          <thead className="bg-slate-100"><tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Item</th><th className="p-2 text-left">HSN</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">GST%</th><th className="p-2 text-right">Amount</th></tr></thead>
          <tbody>
            {t.lines.map((l, i) => (
              <tr key={`${l.name}-${i}`} className="border-t border-slate-200">
                <td className="p-2">{i + 1}</td><td className="p-2">{l.name}</td><td className="p-2">{l.hsn || ""}</td>
                <td className="p-2 text-right">{l.qty} {l.unit}</td><td className="p-2 text-right">{fmtINR(l.price)}</td>
                <td className="p-2 text-right">{t.gst_enabled ? l.gst_rate : 0}</td><td className="p-2 text-right">{fmtINR(l.qty * l.price - (l.discount || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end mt-3">
          <div className="w-64 text-sm font-mono space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmtINR(t.subtotal - t.total_discount)}</span></div>
            {t.cgst > 0 && <div className="flex justify-between"><span>CGST</span><span>{fmtINR(t.cgst)}</span></div>}
            {t.sgst > 0 && <div className="flex justify-between"><span>SGST</span><span>{fmtINR(t.sgst)}</span></div>}
            {t.igst > 0 && <div className="flex justify-between"><span>IGST</span><span>{fmtINR(t.igst)}</span></div>}
            <div className="flex justify-between font-bold border-t border-slate-300 pt-1"><span>Total</span><span data-testid="share-total">{fmtINR(t.total)}</span></div>
          </div>
        </div>
        {t.notes && <div className="mt-4 text-xs">Notes: {t.notes}</div>}
        <div className="mt-10 text-right text-xs">For {b.name}<br /><br />Authorised Signatory</div>
      </div>
    </div>
  );
}
